import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useToast } from '../components/useToast'

export default function MuroSugerencias() {
  const navigate = useNavigate()
  const toast = useToast()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [suggestions, setSuggestions] = useState([])
  const [likedIds, setLikedIds] = useState(new Set())
  const [orderBy, setOrderBy] = useState('recent') // recent | likes

  useEffect(() => {
    async function init() {
      const { data } = await supabase.auth.getUser()
      setUser(data?.user || null)
      await loadAll(data?.user?.id)
      setLoading(false)
    }
    init()
  }, [])

  async function loadAll(currentUserId) {
    const { data: suggData, error } = await supabase
      .from('suggestions')
      .select('*')
      .eq('status', 'approved')

    if (error) { console.error(error); return }

    const { data: likesData } = await supabase
      .from('suggestion_likes')
      .select('suggestion_id, user_id')

    const counts = {}
    const mineLiked = new Set()
    ;(likesData || []).forEach(l => {
      counts[l.suggestion_id] = (counts[l.suggestion_id] || 0) + 1
      if (l.user_id === currentUserId) mineLiked.add(l.suggestion_id)
    })

    const withCounts = (suggData || []).map(s => ({ ...s, likes_count: counts[s.id] || 0 }))
    setSuggestions(withCounts)
    setLikedIds(mineLiked)
  }

  async function toggleLike(suggestionId) {
    if (!user) return
    const alreadyLiked = likedIds.has(suggestionId)

    if (alreadyLiked) {
      await supabase.from('suggestion_likes').delete().eq('suggestion_id', suggestionId).eq('user_id', user.id)
      setLikedIds(prev => { const next = new Set(prev); next.delete(suggestionId); return next })
      setSuggestions(prev => prev.map(s => s.id === suggestionId ? { ...s, likes_count: Math.max(0, s.likes_count - 1) } : s))
    } else {
      const { error } = await supabase.from('suggestion_likes').insert({ suggestion_id: suggestionId, user_id: user.id })
      if (error) { toast.error('Error', 'No se pudo dar like'); return }
      setLikedIds(prev => new Set(prev).add(suggestionId))
      setSuggestions(prev => prev.map(s => s.id === suggestionId ? { ...s, likes_count: s.likes_count + 1 } : s))
    }
  }

  // Las actualizaciones (type === 'actualizacion') siempre van primero,
  // sin importar el orden elegido. El resto se ordena normal.
  const updates = suggestions
    .filter(s => s.type === 'actualizacion')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

  const normales = [...suggestions.filter(s => s.type !== 'actualizacion')].sort((a, b) => {
    if (orderBy === 'likes') return b.likes_count - a.likes_count
    return new Date(b.created_at) - new Date(a.created_at)
  })

  const sorted = [...updates, ...normales]

  function timeAgo(iso) {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'Ahora mismo'
    if (mins < 60) return `Hace ${mins} min`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `Hace ${hrs}h`
    return `Hace ${Math.floor(hrs / 24)}d`
  }

if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20, background: 'var(--color-bg)' }}>
        <div style={{ width: 70, height: 70, borderRadius: 20, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, animation: 'pulse 1.5s infinite' }}>💡</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text)', marginBottom: 6 }}>Cargando el muro</div>
          <div style={{ fontSize: 14, color: 'var(--color-muted)' }}>Reuniendo las ideas de la comunidad...</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: '#6366f1', animation: `bounce 0.6s infinite ${i * 0.15}s` }} />
          ))}
        </div>
        <style>{`
          @keyframes pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.08);opacity:0.85} }
          @keyframes bounce { 0%,80%,100%{transform:translateY(0);opacity:0.5} 40%{transform:translateY(-8px);opacity:1} }
        `}</style>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>

      {/* HEADER */}
      <header style={{ background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/home')}
            style={{ width: 36, height: 36, background: 'rgba(120,120,120,0.08)', border: '1px solid var(--color-border)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 18, color: 'var(--color-text)', transition: '0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(-2px)'; e.currentTarget.style.borderColor = 'var(--color-primary)' }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--color-border)' }}
          >←</button>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)' }}>💡 Muro de sugerencias</div>
            <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>Ideas de la comunidad para mejorar Calypso</div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px 80px' }}>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button
            onClick={() => setOrderBy('recent')}
            style={{
              padding: '8px 14px', borderRadius: 10,
              border: `1px solid ${orderBy === 'recent' ? 'var(--color-primary)' : 'var(--color-border)'}`,
              background: orderBy === 'recent' ? 'var(--color-primary-lt)' : 'var(--color-surface)',
              color: orderBy === 'recent' ? 'var(--color-primary)' : 'var(--color-text)',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >🕒 Recientes</button>
          <button
            onClick={() => setOrderBy('likes')}
            style={{
              padding: '8px 14px', borderRadius: 10,
              border: `1px solid ${orderBy === 'likes' ? 'var(--color-primary)' : 'var(--color-border)'}`,
              background: orderBy === 'likes' ? 'var(--color-primary-lt)' : 'var(--color-surface)',
              color: orderBy === 'likes' ? 'var(--color-primary)' : 'var(--color-text)',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >🔥 Más votadas</button>
        </div>

        {sorted.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 10px', color: 'var(--color-muted)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
            Aún no hay sugerencias aprobadas
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sorted.map(s => {
              const liked = likedIds.has(s.id)
              const isUpdate = s.type === 'actualizacion'
              return (
                <div key={s.id} style={{
                  background: isUpdate ? 'var(--color-primary-lt)' : 'var(--color-surface)',
                  border: isUpdate ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                  borderRadius: 16,
                  padding: 16,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
                    <span style={{
                      fontSize: 12, fontWeight: 700,
                      color: isUpdate ? 'var(--color-primary)' : 'var(--color-primary)',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      {isUpdate && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                          background: 'var(--color-primary)', color: 'white',
                        }}>📢 Actualización</span>
                      )}
                      {!isUpdate && (s.author_name || 'Usuario')}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--color-muted)' }}>{timeAgo(s.created_at)}</span>
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>{s.content}</div>
                  <button
                    onClick={() => toggleLike(s.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      background: liked ? 'rgba(239,68,68,0.12)' : 'var(--color-bg)',
                      border: `1px solid ${liked ? 'rgba(239,68,68,0.3)' : 'var(--color-border)'}`,
                      borderRadius: 999, padding: '6px 12px', cursor: 'pointer',
                      color: liked ? '#ef4444' : 'var(--color-muted)', fontSize: 12, fontWeight: 700,
                    }}
                  >
                    {liked ? '❤️' : '🤍'} {s.likes_count}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}